/**
 * @deprecated This composable is deprecated. Session data is now managed by `useStateMachine.ts`.
 *
 * The state machine consolidates session data tracking. This file is kept for
 * backward compatibility with UserTracking and other external dependencies.
 * It will be removed in a future release.
 */
import { ref } from "vue";

// Session information that should be available globally for tracking
interface SessionInfo {
  sessionId: string | null;
  licenseId: string | null;
  patientId: string | null;
  userId: string | null;
  professionalId: string | null;
  pms: string | null;
  bookingId: string | null;
  recorderSessionId: string | null;
}

// Initialize with null values - will be set when session starts
const sessionInfo = ref<SessionInfo>({
  sessionId: null,
  licenseId: null,
  patientId: null,
  userId: null,
  professionalId: null,
  pms: null,
  bookingId: null,
  recorderSessionId: null,
});

const summaryStatus = ref<string | null>(null);

/** All templates from /templates endpoint (used by form_template_selection) */
const allTemplates = ref<Array<{ id: number; description: string; name?: string; sections?: Record<string, string> }>>([]);

/** Thread count */
const threadCount = ref<number>(0);

/** iPront */
const reloadTimeline = ref<string>("");

export function useSessionStore() {
  return {
    sessionInfo: sessionInfo,

    // Set session information (called when session starts)
    setSessionInfo: (info: Partial<SessionInfo>) => {
      sessionInfo.value = { ...sessionInfo.value, ...info };
    },

    setRecorderSessionId: (recorderSessionId: string) => {
      sessionInfo.value.recorderSessionId = recorderSessionId;
    },

    setSummary: (summary: string) => {
      summaryStatus.value = summary;
    },

    // Clear session information (called when session ends or page refreshes)
    clearSessionInfo: () => {
      sessionInfo.value = {
        sessionId: null,
        licenseId: null,
        patientId: null,
        userId: null,
        professionalId: null,
        pms: null,
        bookingId: null,
        recorderSessionId: null,
      };
    },

    // Get specific session property
    getSessionId: () => sessionInfo.value.sessionId,
    getLicenseId: () => sessionInfo.value.licenseId,
    getPatientId: () => sessionInfo.value.patientId,
    getUserId: () => sessionInfo.value.userId,
    getProfessionalId: () => sessionInfo.value.professionalId,
    getPms: () => sessionInfo.value.pms,
    getBookingId: () => sessionInfo.value.bookingId,
    getRecorderSessionId: () => sessionInfo.value.recorderSessionId,

    getSummary: () => summaryStatus.value,

    // Thread count
    setThreadCount: (newThreadCount: number) => {
      threadCount.value = newThreadCount;
    },
    getThreadCount: () => threadCount.value,

    // iPront
    setReloadTimeline: (newReloadTimeline: string) => {
      reloadTimeline.value = newReloadTimeline;
    },
    getReloadTimeline: () => reloadTimeline.value,

    // All templates (form_template_selection)
    setAllTemplates: (templates: Array<{ id: number; description: string; name?: string; sections?: Record<string, string> }>) => {
      allTemplates.value = templates;
    },
    getAllTemplates: () => allTemplates.value,

    // Check if session is active
    isSessionActive: () => sessionInfo.value.sessionId !== null,
  };
}
